import { Button, Icon } from '@starter/ui';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { layoutArtwork } from '../artwork.ts';
import { useCatalog } from '../catalogs/use-catalog.ts';
import { CreditsPill } from '../credits/pill.tsx';
import { Banner, Quickstart, Shelf } from '../home/blocks.tsx';
import { CreateButton, CreateDialog } from '../home/create-dialog.tsx';
import { quickstart } from '../home/quickstart.ts';
import { useJobs } from '../jobs/api.ts';
import { ImageDoors } from '../jobs/image-doors.tsx';
import { authClient } from '../lib/auth.ts';
import { useProducts } from '../products/api.ts';
import { useVideoOffered, VideoDoors } from '../video/doors.tsx';
import { TemplateShelf } from '../video/shelf.tsx';

/**
 * The home page: the one screen that is not about something you own.
 *
 * It is a shop window — what you could make, what the product offers, and one
 * checklist saying where you stopped — and the order it is in is doing the
 * work: **the checklist is above everything and everything else is optional.**
 * Somebody following it never scrolls; the shelves below are for the person
 * who finished it and came back.
 *
 * The checklist is counted from data rather than from clicks (`home/quickstart.ts`),
 * which is the product's own rule and the reason this page needs no state of
 * its own: it asks what exists and says where that puts you.
 */
function Home() {
  const products = useProducts('active');
  // No polling here: this page wants a count, not a live list, and a home page
  // that re-renders every second is a home page whose dialog cannot be pressed.
  const jobs = useJobs({}, { poll: false });
  const layouts = useCatalog('layout_archetypes');
  const videoOffered = useVideoOffered();

  const rows = products.data?.products ?? [];
  const works = jobs.data?.jobs ?? [];

  const progress = quickstart({
    photographs: rows.reduce((sum, product) => sum + (product.photoCount ?? 0), 0),
    imageAds: works.filter((job) => job.kind === 'static_ad' && job.status === 'done').length,
    /*
     * Counted from what the work left behind rather than from a list of video
     * kinds written here: «which kinds are videos» already has a home, and it
     * is `job_kinds`. The asset's *kind* is the right question for this one —
     * what was made — where the shelf asks its media type, which is the right
     * question for a different one: which element can play it.
     */
    videoAds: works.filter(
      (job) => job.status === 'done' && job.assets.some((asset) => asset.kind === 'video'),
    ).length,
    videoOffered,
  });

  return (
    <div
      className="flex flex-col gap-8 px-4 pt-8 pb-16"
      data-fidelity="home-main"
      data-testid="home-main"
    >
      <header className="flex flex-wrap items-center gap-4 py-2">
        <h1
          className="min-w-0 flex-1 font-display text-[28px] leading-[34px] font-bold tracking-[-0.7px] text-ink"
          data-fidelity="home-title"
          data-testid="home-title"
        >
          Home
        </h1>
        {/* Measured beside this button, and left out until money existed. It does now. */}
        <CreditsPill />
        <CreateButton />
      </header>

      {/* Gone for good once it is finished, which is the product's own rule. */}
      {progress.finished ? null : <Quickstart progress={progress} />}

      <section className="flex flex-col gap-4" data-fidelity="home-doors">
        <h2 className="text-[20px] leading-6 font-bold text-ink">Image ads</h2>
        <ImageDoors testIdPrefix="home-door-" />
      </section>

      {/*
        Fourteen layouts, read from the catalog rather than listed here: the
        shelf is the one block on this page whose contents are configuration
        (`docs/CATALOGS.md`).
      */}
      <Shelf heading="Layouts that are working">
        {layouts.items.map((layout) => (
          /*
            A link, not an article. The shelf named fourteen compositions and
            went nowhere, which the owner found by pressing one on production —
            a tile with a picture and a name reads as a way in, and a way in
            that does nothing is worse than no tile.

            It carries the layout with it, so the samples on the other side
            open already narrowed to the one that was pressed.
          */
          <Link
            className="flex w-[304px] flex-none cursor-pointer flex-col gap-3 rounded-card outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
            data-testid="home-layout-tile"
            key={layout.key}
            search={{ layout: layout.key }}
            to="/image-ads"
          >
            {/*
              The layout's own picture, and here the picture *is* the content:
              «Lifestyle immersion» is a composition, and a shelf of composition
              names with no compositions on it is a glossary. Artwork ours and
              generated (`src/artwork.ts`); a row with none falls back to the
              icon rather than to a broken image.
            */}
            <span className="relative flex h-[218px] w-[264px] items-center justify-center overflow-hidden rounded-card bg-surface-muted text-ink-faint">
              {layoutArtwork(layout.key) ? (
                <img
                  alt=""
                  className="absolute inset-0 size-full object-cover"
                  src={layoutArtwork(layout.key)}
                />
              ) : (
                <Icon name="image" size={28} />
              )}
            </span>
            <span className="w-[264px] text-center text-[16px] leading-[22.4px] font-semibold text-ink">
              {layout.label.en ?? layout.key}
            </span>
          </Link>
        ))}
      </Shelf>

      {videoOffered ? (
        <section className="flex flex-col gap-4" data-fidelity="home-doors">
          <h2 className="text-[20px] leading-6 font-bold text-ink">Video ads</h2>
          {/*
          The same three doors the hub and the «Create» dialog show, from the
          same rows. They were a copy here until the shelf arrived, and the copy
          was already wrong: the showcase had become orderable and this page
          went on showing it greyed out.
        */}
          <VideoDoors testIdPrefix="home-door-" />
        </section>
      ) : null}

      {/*
        «Начните с готовой рекламы» — the block A3 left out because there was
        nothing to put on it (`prd/DIVERGENCE.md`, 2026-09-22). There is now:
        thirty-six ready-made adverts, the same shelf the hub carries.
      */}
      <section className="flex flex-col gap-4" data-testid="home-ready-made">
        <h2 className="text-[20px] leading-6 font-bold text-ink">Start from a ready-made one</h2>
        {videoOffered ? <TemplateShelf /> : null}
      </section>

      <Banner
        action={
          <CreateDialog
            trigger={
              <Button data-testid="home-cta-create-button" variant="primary">
                Make an advert
              </Button>
            }
          />
        }
        body="Turn a photograph of what you sell into still adverts and clips, sized for the places you run them."
        title="Ready to make an advert?"
      />
    </div>
  );
}

export const Route = createFileRoute('/')({
  beforeLoad: async ({ location }) => {
    const { data } = await authClient.getSession();
    /*
     * The funnel, not the form. An anonymous visitor who types the product's
     * address was being asked «who are you» before anything had answered «what
     * is this» — and the answer was already built and already open to
     * everybody, just unreachable without knowing its address (A10).
     *
     * Only this route. A deep link to `/products` still goes to the form and
     * comes back to `/products` afterwards: that person knows what this is and
     * asked for somewhere specific, and walking them through nine questions
     * instead would be worse than the form ever was.
     */
    if (!data) throw redirect({ to: '/start' });
  },
  component: Home,
});
